import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { env } from '@/shared/lib/env';
import { Input } from '@/shared/ui/inputs/Input';
import { Select } from '@/shared/ui/selects/Select';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Section } from '@/shared/ui/layout/Section';
import { useRegionOptions } from '@/shared/utils/useRegionOptions';
import type { AdminParcelFormValues } from '@/features/parcel-form/modules/types';

type Props = { form: UseFormReturn<AdminParcelFormValues> };

export const FeaturesParcelFormSectionAddress = ({ form }: Props) => {
  const { t } = useTranslation();
  const regionOptions = useRegionOptions();
  const { register, control, formState } = form;
  const errors = formState.errors;
  const translateError = (msg?: string) =>
    msg ? t(msg, { length: env.pinflLength }) : undefined;
  return (
    <Section title={t('parcels.form.sections.address')}>
      <Input
        label={t('parcels.form.fields.addressName')}
        required
        error={translateError(errors.address_name?.message)}
        {...register('address_name')}
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Controller
          control={control}
          name="region"
          render={({ field }) => (
            <Select
              label={t('parcels.form.fields.region')}
              required
              options={regionOptions}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={translateError(errors.region?.message)}
            />
          )}
        />
        <Input
          label={t('parcels.form.fields.city')}
          required
          error={translateError(errors.city?.message)}
          {...register('city')}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Input
          label={t('parcels.form.fields.street')}
          required
          error={translateError(errors.street?.message)}
          {...register('street')}
        />
        <Input
          label={t('parcels.form.fields.building')}
          required
          error={translateError(errors.building?.message)}
          {...register('building')}
        />
        <Input
          label={t('parcels.form.fields.apartment')}
          error={translateError(errors.apartment?.message)}
          {...register('apartment')}
        />
      </div>
      <Textarea
        label={t('parcels.form.fields.additionalInfo')}
        rows={3}
        error={translateError(errors.additional_info?.message)}
        {...register('additional_info')}
      />
    </Section>
  );
};
